import { useMemo, useState } from 'react'
import { Check, Copy, Info, Minus, Plus, ShoppingCart, Trash2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AHOrderDialog } from '@/components/menu/ah-order-dialog'
import { BringCartImport } from '@/components/menu/bring-cart-import'
import { aggregateBasket, basketToShoppingListText } from '@/services/menu-service'
import type { BasketItem } from '@/types/menu'

interface MenuBasketPanelProps {
  open: boolean
  onClose: () => void
  items: BasketItem[]
  publicRecipeIds: Set<string>
  onSetServings: (key: string, servings: number) => void
  onRemove: (key: string) => void
  onClear: () => void
}

export function MenuBasketPanel({
  open,
  onClose,
  items,
  publicRecipeIds,
  onSetServings,
  onRemove,
  onClear,
}: MenuBasketPanelProps) {
  const [copied, setCopied] = useState(false)
  const [ahOrderOpen, setAhOrderOpen] = useState(false)

  const aggregatedCount = useMemo(
    () => (open && items.length ? aggregateBasket(items).all_items.length : 0),
    [open, items]
  )

  const bringEligibleIds = useMemo(
    () =>
      items
        .map((item) => item.recipeId)
        .filter((id): id is string => Boolean(id && publicRecipeIds.has(id))),
    [items, publicRecipeIds]
  )

  const notBringEligible = items.filter(
    (item) => !item.recipeId || !publicRecipeIds.has(item.recipeId)
  )

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(basketToShoppingListText(items))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Boodschappen voor je weekmenu</DialogTitle>
          <DialogDescription>
            {items.length} {items.length === 1 ? 'gerecht' : 'gerechten'}
            {aggregatedCount > 0 ? ` · ongeveer ${aggregatedCount} artikelen op je lijst` : ''}
          </DialogDescription>
        </DialogHeader>

        {items.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-600 dark:text-slate-300">
            Je mandje is leeg. Voeg gerechten toe vanuit het weekmenu.
          </p>
        ) : (
          <div className="space-y-4">
            <ul className="space-y-3">
              {items.map((item) => (
                <li
                  key={item.key}
                  className="flex items-center gap-3 rounded-lg border border-gray-200 p-3 dark:border-slate-700"
                >
                  <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-gradient-to-br from-warm-100 to-warm-200">
                    {item.recipe.image_url ? (
                      <img
                        src={item.recipe.image_url}
                        alt={item.recipe.title}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-lg">🍽️</div>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-slate-50">
                      {item.recipe.title}
                    </p>
                    <div className="mt-1 flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => onSetServings(item.key, item.servings - 1)}
                        disabled={item.servings <= 1}
                        aria-label="Minder porties"
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-16 text-center text-xs text-gray-600 dark:text-slate-300">
                        {item.servings} {item.servings === 1 ? 'portie' : 'porties'}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => onSetServings(item.key, item.servings + 1)}
                        aria-label="Meer porties"
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-gray-400 hover:text-red-600"
                    onClick={() => onRemove(item.key)}
                    aria-label={`Verwijder ${item.recipe.title}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>

            <div className="space-y-3 border-t border-gray-200 pt-4 dark:border-slate-700">
              <Button variant="primary" className="w-full" onClick={() => setAhOrderOpen(true)}>
                <ShoppingCart className="mr-2 h-4 w-4" />
                Bestel bij Albert Heijn
              </Button>

              <Button variant="outline" className="w-full" onClick={handleCopy}>
                {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
                {copied ? 'Gekopieerd' : 'Kopieer boodschappenlijst'}
              </Button>

              {bringEligibleIds.length > 0 && (
                <div className="space-y-2">
                  <BringCartImport recipeIds={bringEligibleIds} />
                  <p className="text-xs text-gray-500 dark:text-slate-400">
                    De Bring-import gebruikt de standaardporties van elk recept. De gekopieerde
                    lijst gebruikt jouw porties.
                  </p>
                </div>
              )}

              {notBringEligible.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  <div className="flex items-start gap-2">
                    <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div>
                      Bring kan alleen publieke, opgeslagen recepten importeren.{' '}
                      {notBringEligible.map((item) => item.recipe.title).join(', ')}{' '}
                      {notBringEligible.length === 1 ? 'ontbreekt' : 'ontbreken'} daarom in de
                      Bring-import, maar {notBringEligible.length === 1 ? 'staat' : 'staan'} wel in
                      de gekopieerde lijst.
                    </div>
                  </div>
                </div>
              )}

              <Button variant="ghost" size="sm" className="w-full text-gray-500" onClick={onClear}>
                <Trash2 className="mr-2 h-4 w-4" />
                Leeg mandje
              </Button>
            </div>
          </div>
        )}
      </DialogContent>

      <AHOrderDialog open={ahOrderOpen} onClose={() => setAhOrderOpen(false)} items={items} />
    </Dialog>
  )
}
